'use client';
import { useState, useEffect } from 'react';
import { Clock, Info, MapPin, StickyNote } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { MeetingWithMarkerID } from '@/types.d';
import { isAcceptableConferenceUrl } from '@/src/helpers/admin/conferenceUrl';
import { useCompassContext } from '@/src/libs/CompassContext';
//import bookmark from '@/public/images/meetingCard/bookmark.svg';
//import bookmarkBlack from '@/public/images/meetingCard/bookmark-black.svg';
import { formatDayAndTime } from '@/src/helpers/formatDayAndTime';

import Heading from '../Typography/Heading';
import Button from '../UI/Button';
import Paragraph from '../Typography/Paragraph';

import ReportCard from './ReportCard';

interface MeetingCardProps {
  admin?: boolean;
  focus?: boolean;
  onClick?: () => void;
  meeting: Omit<MeetingWithMarkerID, 'markerId'>;
}

const DetailRow = ({
  icon: Icon,
  label,
  paragraph,
}: {
  icon: LucideIcon;
  label: string;
  paragraph: { style: string; tag?: 'address'; text: string };
}) => (
  <div
    data-testid="icon-and-text"
    className="flex [&:not(:last-child)]:mb-6 items-start"
  >
    <Icon
      aria-label={label}
      className="mr-4 mt-1 shrink-0"
      role="img"
      size={16}
      strokeWidth={1.75}
    />
    <Paragraph {...paragraph} />
  </div>
);

const MeetingCard: React.FC<MeetingCardProps> = ({
  admin = false,
  focus = false,
  onClick,
  meeting,
}): JSX.Element => {
  // const [bookmarkIcon, setBookmarkIcon] = useState(bookmark);
  const [reportCard, setReportCard] = useState(false);
  const cardClasses = `relative px-8 rounded-theme border-2 ${focus ? 'border-black bg-blue p-8 mb-8' : 'group cursor-pointer bg-white border-black/20 hover:border-black focus-visible:border-black hover:bg-black/[.03] focus-visible:bg-black/[.03] py-4 [&:not(:last-child)]:mb-4'} ${reportCard ? 'z-10' : ''}`;
  const paragraphClasses = 'text-base leading-[1.2]';

  const ctx = useCompassContext()!;

  useEffect(() => {
    //setBookmarkIcon(bookmark);
    setReportCard(false);
  }, [ctx?.state?.meetingFocus]);

  // const handleBookmark = () => {
  //   if (bookmarkIcon === bookmark) {
  //     setBookmarkIcon(bookmarkBlack);
  //   } else {
  //     setBookmarkIcon(bookmark);
  //   }
  // };

  const handleCloseReportCard = () => {
    setReportCard(false);
  };

  return (
    <>
      {focus ? (
        <article
          data-testid="meeting-card"
          onClick={() => {
            if (onClick) {
              onClick();
            }
          }}
          className={cardClasses}
        >
          {reportCard ? (
            <ReportCard onClose={handleCloseReportCard} meeting={meeting} />
          ) : (
            <>
              {/* <div
                onClick={handleBookmark}
                className="absolute top-6 right-2 cursor-pointer"
              >
                <Image
                  className="mr-4"
                  alt="Save meeting"
                  height={16}
                  src={bookmarkIcon}
                  width={16}
                />
              </div> */}
              <div className="mb-6">
                <Heading level="h3" style="mr-6" text={meeting.name} />
              </div>
              <DetailRow
                icon={MapPin}
                label="Meeting address"
                paragraph={{
                  style: paragraphClasses,
                  tag: 'address',
                  text: meeting.address,
                }}
              />
              <DetailRow
                icon={Info}
                label="Meeting type"
                paragraph={{
                  style: paragraphClasses + ' font-bold',
                  text: meeting.type,
                }}
              />
              <DetailRow
                icon={Clock}
                label="Meeting time"
                paragraph={{
                  style: paragraphClasses,
                  text: formatDayAndTime(meeting.day, meeting.time),
                }}
              />
              {meeting.note && (
                <DetailRow
                  icon={StickyNote}
                  label="Meeting note"
                  paragraph={{
                    style: paragraphClasses,
                    text: meeting.note,
                  }}
                />
              )}
              {!admin && (
                <div className="flex flex-wrap gap-4">
                  {isAcceptableConferenceUrl(meeting.conferenceUrl) && (
                    <Button
                      ariaLabel="Join this meeting online"
                      href={meeting.conferenceUrl}
                      target="_blank"
                      title="Join online"
                    />
                  )}
                  <Button
                    ariaLabel="Get directions to this meeting"
                    href={`https://maps.google.com/?q=${encodeURIComponent(meeting.address)}`}
                    target="_blank"
                    title="Get directions"
                  />
                  <Button
                    ariaLabel="Report a problem with this meeting"
                    onClick={() => {
                      setReportCard(true);
                    }}
                    title="Report a problem"
                  />
                </div>
              )}
            </>
          )}
        </article>
      ) : (
        <button
          data-testid="meeting-card"
          onClick={() => {
            if (onClick) {
              onClick();
            }
          }}
          className={twMerge(
            cardClasses,
            'w-full text-left focus-visible:outline-none'
          )}
        >
          <Heading
            level="h4"
            style="text-sm font-semibold text-charcoal group-hover:text-black group-focus-visible:text-black"
            text={meeting.name}
          />
        </button>
      )}
    </>
  );
};
export default MeetingCard;
